// APP_DISPATCH table — flat URL → page lookup.
// First match wins. Rules are ordered from most specific to most general.
//
// Sub-app pages live in the unified `pages` map (built in context.js from
// every sub-app's pages/index.js) and the smbls 3.14 auto-router mounts
// the matched page directly into Slot via `customRouterElement: ['Slot']`.
// dispatchUrl no longer Slot.set()s sub-app wrappers — that double-mount
// was burying matched pages under the wrapper's own children (e.g. canvas
// /studio/* rendered only the canvas wrapper chrome, never the studios
// page itself). Wrappers' cross-cutting chrome (AppAssistant, ChatDrawer,
// InstallableModalStub) lives on the workspace shell's app.js so it's
// available regardless of which sub-app's page is active.
//
// dispatchUrl keeps `page:` entries for shell-direct shared pages that
// AREN'T in the sub-app pages map (auth, account, admin, etc.).

export const APP_DISPATCH = [
  // Shell-direct pages — these aren't in sub-app pages maps because
  // they're shared chrome surfaces (login, account settings, admin).
  { match: ['/login', '/signup', '/forgot-password', '/verify-email'], page: 'auth' },
  { match: ['/profile', '/orgs'],                                       page: 'account' },
  { match: /^\/account(\/|$)/,                                          page: 'account' },
  { match: /^\/admin(\/|$)/,                                            page: 'admin' },
  { match: ['/workspaces'],                                             page: 'workspaces' },
  { match: ['/projects'],                                               page: 'projects' }
]

/**
 * Match a URL path against the dispatch table.
 *
 * @param {string} path
 * @param {Array}  table - defaults to APP_DISPATCH
 * @returns {object|null} matched entry or null
 */
export const matchDispatch = (path, table = APP_DISPATCH) => {
  for (const entry of table) {
    const m = entry.match
    if (Array.isArray(m) && m.includes(path)) return entry
    if (typeof m === 'string' && m === path) return entry
    if (m instanceof RegExp && m.test(path)) return entry
  }
  return null
}

// PAGE_IMPORTS maps page keys to async import factories.
// Each factory returns the page DOMQL object (named export).
// Use only the named exports each module actually has — Parcel does
// static analysis on dynamic-import .then(m => m.X) and fails the
// build when X doesn't exist (e.g. login.js has no `default` export).
export const PAGE_IMPORTS = {
  auth: () => import('../shared/pages/auth/login.js').then(m => m.login),
  account: () => import('../shared/pages/account/index.js').then(m => m.account),
  admin: () => import('../shared/pages/admin/index.js').then(m => m.admin),
  workspaces: () => import('../shared/pages/profile.js').then(m => m.profile),
  projects: () => import('../shared/pages/profile.js').then(m => m.profile)
}

// APP_IMPORTS maps app keys to async import factories.
// Returns the standalone app definition for mounting into the Slot.
export const APP_IMPORTS = {
  workspaceProject: () => import('../workspace-project/app.js').then(m => m.default || m),
  canvas: () => import('../canvas/app.js').then(m => m.default || m),
  marketplace: () => import('../marketplace/app.js').then(m => m.default || m),
  preview: () => import('../preview/app.js').then(m => m.default || m)
}

/**
 * Dispatch a URL to the shell's Slot element.
 *
 * For page routes: render the imported page component directly.
 * For app routes: lazy-import and mount the sub-app.
 *
 * @param {object} shell - the root shell element returned by create()
 * @param {string} path  - current URL path
 * @returns {Promise<void>}
 */
export const dispatchUrl = async (shell, path) => {
  const target = matchDispatch(path)
  if (!target) {
    // 404: render nothing (slot is empty)
    return
  }

  if (target.page) {
    const importFn = PAGE_IMPORTS[target.page]
    if (importFn) {
      const page = await importFn().catch(() => null)
      if (page && shell.Slot) shell.Slot.set?.(page)
    }
    return
  }

  if (target.app) {
    const importFn = APP_IMPORTS[target.app]
    if (importFn) {
      const appDef = await importFn().catch(() => null)
      if (appDef && shell.Slot) shell.Slot.set?.(appDef)
    }
  }
}
